import React, { Component } from 'react';
import moment from 'moment';
import {
  caretBackCircleOutline as caretLeft,
  caretForwardCircleOutline as caretRight,
  closeOutline as close,
} from 'ionicons/icons';

import {
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonTitle,
  IonLabel,
  IonIcon,
  IonChip,
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonFooter,
  IonTextarea,
  withIonLifeCycle,
  IonLoading,
  IonText,
  IonList,
  IonCheckbox,
  IonSpinner,
} from '@ionic/react';

import RentalDevicesService from '../services/rentalDevices';
import RentalReservationService from '../services/rentalReservations';
import AuthResource from '../resources/Auth';

import { rentalTotal } from '../utils/rentalPrice';
import { preselectedDate, rentalPath } from '../utils/preselectedDate';

class RentalCalendar extends Component {
  constructor(props) {
    super(props);

    this.selectedTimeRef = React.createRef('');
    this.pickedDateRef = React.createRef('');
    this.noteRef = React.createRef('');

    this.state = {
      availableDates: [],
      windowEnd: null,
      pickedDateTime: null,
      isModalOpen: false,
      isActive: '',
      calendarDayProgression: [],
      loading: false,
      loadingSlots: false,
      device: null,
      firstSlot: null,
      allSlots: [],
      duration: props.duration || 1,
      submitting: false,
      termsAccepted: false,
    };
  }

  async componentDidMount() {
    await this.loadDevice();
    this.setCalendarLimitsDate();
    this.applyPreselectedDate();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.duration !== this.props.duration) {
      await this.setState({ duration: this.props.duration || 1 });
      this.reset();
      await this.loadDevice();
      this.applyPreselectedDate();
    }
  }

  async loadDevice() {
    const { deviceId } = this.props;
    const { duration } = this.state;

    if (!deviceId) return;

    try {
      const { data: device } = await RentalDevicesService.findOne(deviceId);
      this.setState({ device });

      const { data: datesData } = await RentalReservationService.availableDates({
        id: deviceId,
        duration,
      });

      this.setState({
        availableDates: datesData.available_dates || [],
        windowEnd: datesData.window_end || null,
      });
    } catch (e) {
      console.error(e);
    }
  }

  async fetchAvailability(fullDate) {
    const { deviceId } = this.props;
    const { duration } = this.state;

    // Gli orari del giorno precedente si tolgono subito, così lo spinner
    // mostra che si stanno caricando quelli del giorno scelto.
    this.setState({ loadingSlots: true, pickedDateTime: null, allSlots: [], firstSlot: null });

    // Con una risposta veloce lo spinner comparirebbe per pochi millisecondi
    // senza essere visto: resta a schermo almeno per questo tempo.
    const minimumLoading = new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const [{ data: data }] = await Promise.all([
        RentalReservationService.availability(deviceId, fullDate, duration),
        minimumLoading,
      ]);

      if (data.slots && data.slots.length > 0) {
        this.setState({
          pickedDateTime: data.slots,
          allSlots: data.slots,
          firstSlot: data.slots[0],
        });
      } else {
        this.setState({
          pickedDateTime: [],
          allSlots: [],
          firstSlot: null,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({ loadingSlots: false });
    }
  }

  // Link del chatbot con ?date=: se il giorno è fra quelli offerti per la durata
  // corrente, il calendario si porta sul suo mese e lo seleziona, con gli orari.
  applyPreselectedDate() {
    const giorno = preselectedDate(this.props.location?.search, this.state.availableDates);
    if (!giorno) return;

    const mese = moment(giorno);
    if (this.props.goToMonth) this.props.goToMonth(mese);
    this.setCalendarLimitsDate(mese);
    this.handleDateClick(giorno, false);
  }

  handleDateClick(fullDate, isDisabled) {
    if (isDisabled) return;
    this.setState({ isActive: fullDate });
    this.pickedDateRef.current = fullDate;
    this.fetchAvailability(fullDate);
  }

  reset() {
    this.pickedDateRef.current = '';
    this.setState({ pickedDateTime: null, isActive: '', firstSlot: null, allSlots: [] });
  }

  async submitBooking() {
    const { deviceId, onBookingComplete } = this.props;
    const { device, duration } = this.state;
    const isLogged = await AuthResource.isLogged;

    if (!isLogged) {
      this.setState({ isModalOpen: false, submitting: false });
      const redirectUrl = rentalPath(deviceId, this.state.isActive);
      this.props.history.push(`/auth/login?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    this.setState({ submitting: true });

    try {
      const { data: result } = await RentalReservationService.saveReservation({
        rental_device_id: deviceId,
        start_day: this.pickedDateRef.current,
        duration: parseInt(duration, 10),
        start_appointment: this.selectedTimeRef.current,
        note: this.noteRef.current,
        terms_accepted: this.state.termsAccepted,
      });

      if (result.success) {
        this.setState({ isModalOpen: false, submitting: false });
        if (this.props.toast?.current) {
          this.props.toast.current.add({
            message: device?.require_confirmation
              ? 'Richiesta inviata! Riceverai una conferma dalla farmacia.'
              : 'Prenotazione confermata!',
          });
        }

        if (onBookingComplete) {
          onBookingComplete();
        } else {
          this.props.history.push('/app/rentals');
        }
      } else {
        this.setState({ submitting: false });
        if (this.props.toast?.current) {
          this.props.toast.current.add({
            message: result.error || 'Errore durante la prenotazione.',
            color: 'danger',
          });
        }
      }
    } catch (e) {
      console.error(e);
      this.setState({ submitting: false });
      if (this.props.toast?.current) {
        this.props.toast.current.add({
          message: 'Errore di connessione. Riprova.',
          color: 'danger',
        });
      }
    }
  }

  goToFirstAvailable() {
    this.props.history.push(`/app/rentals/${this.props.deviceId}?tab=calendar`);
  }

  setCalendarLimitsDate(month = this.props.momentDate) {
    const arr = [];
    let _arr = [];
    let startOfCalendar = month.clone().startOf('month');
    let endOfCalendar = month.clone().endOf('month');
    let startOfMonthNum = Number(startOfCalendar.format('d')) - 1;
    let endOfMonthNum = Number(endOfCalendar.format('d')) - 1;

    startOfCalendar = this.setCalendarLimits(startOfCalendar, startOfMonthNum, true);
    endOfCalendar = this.setCalendarLimits(endOfCalendar, endOfMonthNum, false);

    while (startOfCalendar.format('YYYY-MM-DD') !== endOfCalendar.format('YYYY-MM-DD')) {
      _arr.push(startOfCalendar.clone());
      if (Number(startOfCalendar.format('d')) === 0) {
        arr.push(_arr);
        _arr = [];
      }
      startOfCalendar = startOfCalendar.add(1, 'd');
    }

    this.setState({ calendarDayProgression: arr });
  }

  setCalendarLimits(obj, num, bool) {
    if (num === -1) num = 6;
    if (bool) {
      if (num !== 0) obj = obj.subtract(num, 'd');
    } else {
      if (num !== 6) obj = obj.add(7 - num, 'd');
      else if (num === 6) obj = obj.add(1, 'd');
    }
    return obj;
  }

  render() {
    // Un dispositivo senza termini può arrivare con null o con una stringa di soli
    // spazi: in entrambi i casi non c'è niente da far accettare.
    const bookingTerms = (this.state.device?.booking_terms || '').trim();

    const {
      availableDates,
      windowEnd,
      isActive,
      pickedDateTime,
      firstSlot,
      allSlots,
      isModalOpen,
      device,
      submitting,
      termsAccepted,
      loadingSlots,
    } = this.state;
    const today = moment().format('YYYY-MM-DD');

    // Il calendario non va oltre il mese dell'ultimo giorno prenotabile.
    const nextMonthStart = this.props.momentDate.clone().add(1, 'month').startOf('month');
    const atLastMonth = !!windowEnd && nextMonthStart.format('YYYY-MM-DD') > windowEnd;

    return (
      <>
        <IonGrid>
          <IonRow className="ion-margin-vertical">
            <IonCol size="1">
              <IonIcon
                icon={caretLeft}
                style={{ height: '30px', width: '30px' }}
                onClick={() => {
                  this.props.prevMonth();
                  this.setCalendarLimitsDate();
                  this.reset();
                }}
              />
            </IonCol>

            <IonCol>
              <IonTitle className="ion-text-uppercase ion-text-center">
                {this.props.momentDate.format('MMMM YYYY')}
              </IonTitle>
            </IonCol>

            <IonCol className="ion-padding-end" size="1">
              <IonIcon
                className="ion-float-end"
                icon={caretRight}
                style={{ height: '30px', width: '30px', opacity: atLastMonth ? 0.3 : 1 }}
                onClick={() => {
                  // Il calendario non va oltre il mese dell'ultimo giorno prenotabile.
                  const nextMonthStart = this.props.momentDate.clone().add(1, 'month').startOf('month');
                  if (windowEnd && nextMonthStart.format('YYYY-MM-DD') > windowEnd) return;

                  this.props.nextMonth();
                  this.setCalendarLimitsDate();
                  this.reset();
                }}
              />
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonItem className="ion-no-margin ion-no-padding">
          <table style={{ width: '100%', margin: 'auto auto 10px auto' }}>
            <thead>
              <tr>
                <th>Lun</th>
                <th>Mar</th>
                <th>Mer</th>
                <th>Gio</th>
                <th>Ven</th>
                <th>Sab</th>
                <th>Dom</th>
              </tr>
            </thead>

            <tbody>
              {this.state.calendarDayProgression.map((weekDate) => {
                return (
                  <tr key={weekDate[0]?.format('MM-DD')}>
                    {weekDate.map((dayDate) => {
                      const day = dayDate.format('DD');
                      const fullDate = dayDate.format('YYYY-MM-DD');
                      const isPast = fullDate < today;
                      const isDisabled = isPast || availableDates.indexOf(fullDate) === -1;
                      let color = 'light';

                      if (!isDisabled) {
                        color = isActive === fullDate ? 'middle' : 'dark';
                      }

                      return (
                        <td key={fullDate}>
                          <IonChip
                            id={fullDate}
                            color={color}
                            outline={true}
                            disabled={isDisabled}
                            onClick={() => this.handleDateClick(fullDate, isDisabled)}
                          >
                            <IonLabel>{day}</IonLabel>
                          </IonChip>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </IonItem>

        {loadingSlots && (
          <div className="ion-text-center ion-padding">
            <IonSpinner />
          </div>
        )}

        {pickedDateTime && (
          <div style={{ padding: '0 16px' }}>
            {allSlots.length === 0 ? (
              <IonText color="medium">
                <p style={{ textAlign: 'center' }}>Nessuna fascia oraria disponibile per questa data.</p>
              </IonText>
            ) : (
              <div>
                <IonLabel>
                  <h3 style={{ marginBottom: '8px' }}>
                    {/* {allSlots.length === 1 ? 'Primo orario disponibile' : 'Orari disponibili'} */}
                    Orari disponibili per il ritiro del {moment(this.pickedDateRef.current).format('DD/MM/YYYY')}
                  </h3>
                </IonLabel>

                {/* {isActive && firstSlot && (
                  <div style={{ marginBottom: '16px' }}>
                    <IonChip
                      color="success"
                      style={{ fontWeight: 'bold' }}
                      onClick={() => {
                        this.setState({ isModalOpen: true });
                        this.selectedTimeRef.current = firstSlot.time;
                      }}
                    >
                      <IonLabel>
                        {firstSlot.label}
                        {firstSlot.period === 'morning' ? ' ☀️' : ' 🌤️'}
                      </IonLabel>
                    </IonChip>
                  </div>
                )} */}

                {/* {allSlots.length > 1 && ( */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {allSlots.map((slot, index) => (
                    <IonChip
                      key={index}
                      color={'success'}
                      outline={true}
                      onClick={() => {
                        this.setState({ isModalOpen: true, termsAccepted: false });
                        this.selectedTimeRef.current = slot.time;
                      }}
                    >
                      <IonLabel>{slot.time}</IonLabel>
                    </IonChip>
                  ))}
                </div>
                {/* )} */}
              </div>
            )}
          </div>
        )}

        {!pickedDateTime && !isActive && (
          <IonText color="medium">
            <p style={{ textAlign: 'center', marginTop: '16px' }}>
              Seleziona un giorno per vedere gli orari di ritiro disponibili.
            </p>
          </IonText>
        )}

        {device && (
          <IonModal
            isOpen={isModalOpen}
            onDidDismiss={() => this.setState({ isModalOpen: false, termsAccepted: false })}
          >
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="end">
                  <IonButton onClick={() => this.setState({ isModalOpen: false, termsAccepted: false })}>
                    <IonIcon icon={close} />
                  </IonButton>
                </IonButtons>
                <IonTitle>Riepilogo Noleggio</IonTitle>
              </IonToolbar>
            </IonHeader>

            <IonContent fullscreen>
              <IonList>
                <ion-item-group>
                  <ion-item-divider>
                    <IonLabel color="dark">
                      <b>Dispositivo</b>
                    </IonLabel>
                  </ion-item-divider>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>{device.name}</h4>
                      {device.category_name && <p>Categoria: {device.category_name}</p>}
                    </IonLabel>
                  </IonItem>

                  {device.show_price !== false && (
                    <IonItem lines="none">
                      <IonLabel>Prezzo</IonLabel>
                      <IonText slot="end">
                        {(() => {
                          // Il totale deve coincidere con quello che l'API
                          // salvera' davvero: prima qui c'era una copia della
                          // regola, e per le durate oltre i tre giorni mostrava
                          // una cifra diversa da quella addebitata.
                          const d = this.state.duration;
                          const total = rentalTotal(device, d);

                          if (!total) return 'Gratuito';

                          if (device.price_type === 'single') return `${total.toFixed(2)}€`;

                          return `${total.toFixed(2)}€ per ${d} ${d === 1 ? 'giorno' : 'giorni'}`;
                        })()}
                      </IonText>
                    </IonItem>
                  )}
                </ion-item-group>

                <ion-item-group>
                  <ion-item-divider>
                    <IonLabel color="dark">
                      <b>Dettaglio Prenotazione</b>
                    </IonLabel>
                  </ion-item-divider>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>Data ritiro:</h4>
                      <p>{moment(this.pickedDateRef.current).format('dddd | DD MMMM YYYY')}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>Ora ritiro:</h4>
                      <p>{this.selectedTimeRef.current}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>Durata:</h4>
                      <p>
                        {this.state.duration} giorno{this.state.duration > 1 ? 'i' : ''}
                      </p>
                    </IonLabel>
                  </IonItem>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>Data restituzione:</h4>
                      <p>
                        {moment(this.pickedDateRef.current)
                          .add(this.state.duration, 'days')
                          .format('dddd | DD MMMM YYYY')}
                      </p>
                    </IonLabel>
                  </IonItem>

                  <IonItem lines="none">
                    <IonLabel>
                      <h4>Politica di cancellazione:</h4>
                      <p>
                        {device.cancel_advance_days === 999
                          ? 'Una volta prenotato il dispositivo, non sarà più possibile cancellare questa prenotazione!'
                          : device.cancel_advance_days === 0
                            ? 'La prenotazione è sempre cancellabile!'
                            : 'La prenotazione è cancellabile fino al giorno ' +
                              moment(this.pickedDateRef.current)
                                .add(device.cancel_advance_days, 'days')
                                .format('DD MMMM YYYY')}
                      </p>
                    </IonLabel>
                  </IonItem>
                </ion-item-group>

                <ion-item-group>
                  <ion-item-divider>
                    <IonLabel color="dark">
                      <b>Note</b>
                    </IonLabel>
                  </ion-item-divider>

                  <IonItem lines="none">
                    <IonTextarea
                      placeholder="Note aggiuntive per la farmacia..."
                      value={this.noteRef.current}
                      onIonChange={(e) => (this.noteRef.current = e.detail.value)}
                      rows={3}
                    />
                  </IonItem>
                </ion-item-group>

                {!!device.require_confirmation && (
                  <IonItem lines="none">
                    <IonText color="warning">
                      <p style={{ fontSize: '13px' }}>
                        ⚠️ La prenotazione richiederà conferma da parte della farmacia.
                      </p>
                    </IonText>
                  </IonItem>
                )}

                {/*
                  I termini vanno letti prima di essere accettati, quindi il testo sta
                  sopra e la checkbox porta un'etichetta breve: con termini lunghi una
                  label integrale diventerebbe un muro di testo cliccabile.
                */}
                {!!bookingTerms && (
                  <ion-item-group>
                    <ion-item-divider>
                      <IonLabel className="ion-text-uppercase" color="dark">
                        <b>Termini e condizioni</b>
                      </IonLabel>
                    </ion-item-divider>

                    <IonItem lines="none">
                      <IonText className="ion-text-wrap">
                        <p style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{bookingTerms}</p>
                      </IonText>
                    </IonItem>

                    <IonItem lines="none">
                      <IonCheckbox
                        slot="start"
                        checked={termsAccepted}
                        onIonChange={(e) => this.setState({ termsAccepted: e.detail.checked })}
                      />
                      <IonLabel className="ion-text-wrap">Accetto i termini e le condizioni</IonLabel>
                    </IonItem>
                  </ion-item-group>
                )}
              </IonList>
            </IonContent>

            <IonFooter>
              <IonToolbar>
                <IonButton
                  className="ion-margin-horizontal"
                  expand="block"
                  color="success"
                  onClick={() => this.submitBooking()}
                  disabled={submitting || (!!bookingTerms && !termsAccepted)}
                >
                  {submitting ? 'Salvataggio...' : 'Conferma Prenotazione'}
                </IonButton>
              </IonToolbar>
            </IonFooter>

            <IonLoading isOpen={submitting} message="Salvataggio in corso..." duration={0} />
          </IonModal>
        )}
      </>
    );
  }
}

export default withIonLifeCycle(RentalCalendar);
